import { useState } from 'react';
import { Box, Collapse, Divider, Drawer, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { IoChevronDown, IoChevronUp, IoClose } from 'react-icons/io5';
import { MdInfoOutline, MdOutlineCalendarToday } from 'react-icons/md';
import Button from 'components/Common/Button';
import { BusinessAddon } from '@/apis/business/addons/types';
import { ADDON_DRAWER_WIDTH, formatCommitmentLabel, getAddonDisplayTitle, isAddonActive, num } from './addonDisplay';

interface AddonTermsDrawerProps {
  addon: BusinessAddon | null;
  open: boolean;
  onClose: () => void;
  onActivate: () => void;
}

function money(value: number | string | undefined): string {
  const amount = Number(value) || 0;
  if (Number.isInteger(amount)) return `$${amount}`;
  return `$${amount.toFixed(2)}`;
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography fontSize={12} fontWeight={500} color='text.secondary' letterSpacing={0.4} mb={1.25}>
      {children}
    </Typography>
  );
}

function DueTodayBadge() {
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: '100px',
        bgcolor: '#F3F4F6',
        color: 'text.secondary',
        fontSize: 11,
        fontWeight: 500,
        ml: 1,
        whiteSpace: 'nowrap'
      }}
    >
      <MdOutlineCalendarToday size={12} />
      Due Today
    </Box>
  );
}

function ChargeRow({
  label,
  amount,
  helper,
  showDueToday,
  borderBottom = true,
  divider = false
}: {
  label: string;
  amount: string;
  helper?: string;
  showDueToday?: boolean;
  borderBottom?: boolean;
  divider?: boolean;
}) {
  return (
    <>
      <Box
        px={2}
        py={1.75}
        borderBottom={borderBottom ? '1px solid #ECECEC' : 'none'}
        display='flex'
        justifyContent='space-between'
        alignItems='flex-start'
        gap={2}
      >
        <Box minWidth={0}>
          <Typography fontSize={14} fontWeight={500} display='flex' alignItems='center' flexWrap='wrap'>
            {label}
            {showDueToday && <DueTodayBadge />}
          </Typography>

          {helper && (
            <Typography fontSize={12} color='text.secondary' mt={0.35} lineHeight={1.45}>
              {helper}
            </Typography>
          )}
        </Box>
        <Typography fontSize={14} fontWeight={500} whiteSpace='nowrap'>
          {amount}
        </Typography>
      </Box>
      {divider && <Divider sx={{ mx: 1.5, color: '#f3f3f3' }} />}
    </>
  );
}

function CommitmentRow({ label }: { label: string }) {
  return (
    <Box bgcolor='#F9FAFB' px={2} py={1.75} display='flex' justifyContent='space-between' alignItems='center' gap={2}>
      <Typography fontSize={14} color='text.secondary'>
        Commitment
      </Typography>
      <Typography fontSize={14} fontWeight={500}>
        {label}
      </Typography>
    </Box>
  );
}

function InfoNote({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  return (
    <Stack direction='row' gap={1} alignItems='flex-start' mt={1.5}>
      <MdInfoOutline size={16} color={theme.palette.text.secondary} style={{ marginTop: 2, flexShrink: 0 }} />
      <Typography fontSize={12} color='text.secondary' lineHeight={1.5}>
        {children}
      </Typography>
    </Stack>
  );
}

function ChargeCard({ children }: { children: React.ReactNode }) {
  return (
    <Box border='1px solid #ECECEC' borderRadius={2} overflow='hidden'>
      {children}
    </Box>
  );
}

function PrioritySupportHowCharged({ addon }: { addon: BusinessAddon }) {
  const p = addon.prices || {};
  const setup = num(p.one_time_setup_fee);
  const perLocation = num(p.price_per_location);
  const minLocations = Math.max(num(p.minimum_locations_billed), 1);

  return (
    <>
      <ChargeCard>
        {setup > 0 && (
          <ChargeRow label='One-Time Setup Fee' amount={money(setup)} helper='Charged once activated' showDueToday />
        )}
        <ChargeRow
          label='Priority Support'
          amount={`${money(perLocation)}/location/month`}
          helper={`Minimum ${minLocations} location · billed with your monthly subscription`}
          borderBottom={false}
        />
        <CommitmentRow label={formatCommitmentLabel(addon.commitment_months)} />
      </ChargeCard>
      <InfoNote>
        Setup is charged at activation. Location fees are prorated on your monthly invoice for the activation month,
        then billed in full each month for every active location until cancelled.
      </InfoNote>
    </>
  );
}

function SmsHostingHowCharged({ addon }: { addon: BusinessAddon }) {
  const p = addon.prices || {};
  const setup = num(p.one_time_setup_fee);
  const monthly = num(p.price_per_hosted_number);

  return (
    <>
      <ChargeCard>
        <ChargeRow
          label='One-Time Setup Fee'
          amount={money(setup)}
          helper={setup > 0 ? 'Charged once activated' : 'Waived by your agency'}
          showDueToday={setup > 0}
        />
        <ChargeRow
          label='SMS Hosting'
          amount={`${money(monthly)}/hosted number/month`}
          helper='Charged for every number you host'
          borderBottom={false}
        />
        <CommitmentRow label={formatCommitmentLabel(addon.commitment_months)} />
      </ChargeCard>
      <InfoNote>
        Each additional number you host adds the monthly fee pro-rata. Standard carrier messaging rates from your
        provider may still apply. Charged pro-rata for the current billing cycle, then monthly for every hosted number.
      </InfoNote>
    </>
  );
}

function A2pHowCharged({ addon }: { addon: BusinessAddon }) {
  const p = addon.prices || {};
  const setup = num(p.one_time_setup_fee);
  const vettingFee = num(p.vetting_fee_per_campaign);
  const lowVolume = num(p.low_volume_mixed_campaign);
  const mixed = num(p.mixed_campaign_per_month);
  const maxNumbers = num(p.max_numbers_per_campaign) || 10;

  return (
    <>
      <ChargeCard>
        <ChargeRow
          label='One-Time Setup Fee'
          amount={money(setup)}
          helper='Charged once activated'
          showDueToday
          borderBottom={vettingFee > 0}
        />
        {vettingFee > 0 && (
          <ChargeRow
            label='Vetting Fee'
            amount={money(vettingFee)}
            helper='Charged for each campaign submission and resubmission.'
            borderBottom={false}
          />
        )}
        <CommitmentRow label={formatCommitmentLabel(addon.commitment_months)} />
      </ChargeCard>
      <InfoNote>
        Carrier pass-through fees are billed at cost. A minimum commitment term applies as configured by your agency.
        Charges are prorated for the current billing cycle, then billed monthly for each campaign.
      </InfoNote>

      <Box mt={3}>
        <SectionHeading>BUSINESSES CAN CHOOSE FROM THE CAMPAIGN TYPES BELOW BASED ON THEIR NEEDS</SectionHeading>
        <ChargeCard>
          <ChargeRow
            label='Low-Volume Mixed Campaign'
            amount={`${money(lowVolume)}/campaign/month`}
            borderBottom={false}
            divider
            helper='Conversational messaging appointment, reminder and reply traffic.'
          />
          <ChargeRow
            label='Mixed Campaign'
            amount={`${money(mixed)}/campaign/month`}
            helper='Marketing and bulk messaging with higher carrier throughput.'
            borderBottom={false}
          />
        </ChargeCard>
        <InfoNote>
          You are billed for the selected campaign only — not both. The campaign covers all numbers registered under it
          (up to {maxNumbers}).
        </InfoNote>
      </Box>
    </>
  );
}

function AiWorkflowHowCharged({ addon }: { addon: BusinessAddon }) {
  const p = addon.prices || {};
  const setup = num(p.one_time_setup_fee);
  const monthly = num(p.monthly_price_all_locations);
  const included = num(p.included_runs_per_month);
  const perRun = num(p.price_per_additional_run);

  return (
    <>
      <ChargeCard>
        <ChargeRow
          label='One-Time Setup Fee'
          amount={money(setup)}
          helper={setup > 0 ? 'Charged once activated' : 'Waived by your agency'}
          showDueToday={setup > 0}
        />
        <ChargeRow
          label='Workflow (AI Admin)'
          amount={`${money(monthly)}/month`}
          helper={`Includes ${included.toLocaleString()} runs / month across all locations`}
        />
        <ChargeRow
          label='Additional Runs'
          amount={`${money(perRun)}/run`}
          helper={`Only after the included ${included.toLocaleString()} runs`}
          borderBottom={false}
        />
        <CommitmentRow label={formatCommitmentLabel(addon.commitment_months)} />
      </ChargeCard>
      <InfoNote>
        Setup is charged at activation. The monthly fee (prorated in the activation month), included runs, and any
        overage are billed with your monthly subscription. Run usage resets each cycle and is never carried over.
        Monitor usage under Settings → Usage.
      </InfoNote>
    </>
  );
}

function ConcurrencyLimitHowCharged({ addon }: { addon: BusinessAddon }) {
  const p = addon.prices || {};
  const setup = num(p.one_time_setup_fee);
  const perConcurrency = num(p.price_per_concurrency);

  return (
    <>
      <ChargeCard>
        <ChargeRow
          label='One-Time Setup Fee'
          amount={money(setup)}
          helper={setup > 0 ? 'Charged once activated' : 'Waived by your agency'}
          showDueToday={setup > 0}
        />
        <ChargeRow
          label='Concurrency Charge'
          amount={money(perConcurrency)}
          helper='Monthly charge per concurrency · On top of package included concurrency per location'
          borderBottom={false}
        />
        <CommitmentRow label={formatCommitmentLabel(addon.commitment_months)} />
      </ChargeCard>
      <InfoNote>
        Charges are prorated based on your concurrency limit and billed at the end of the billing cycle. Reducing the
        limit will not adjust charges for the current billing cycle.
      </InfoNote>
    </>
  );
}

// const CONCURRENCY_DEFAULT_TERMS = (
//   <Box
//     sx={{
//       fontSize: 13,
//       color: 'text.secondary',
//       lineHeight: 1.55,
//       '& h4': { color: 'text.primary', fontSize: 14, fontWeight: 500, mt: 2, mb: 1, '&:first-of-type': { mt: 0 } },
//       '& ul': { pl: 2.25, m: 0, mb: 1.5 },
//       '& li': { mb: 0.75 }
//     }}
//   >
//     <Typography component='h4' variant='inherit'>
//       What&apos;s included
//     </Typography>
//     <Box component='ul'>
//       <li>Answer more calls at the same time during peak hours without callers hitting a busy line.</li>
//       <li>Extra capacity applies across all your numbers and AI agents.</li>
//       <li>Raise or lower your limit anytime from the Add-ons page.</li>
//     </Box>
//     <Typography component='h4' variant='inherit'>
//       Good to know
//     </Typography>
//     <Box component='ul'>
//       <li>When all lines are busy, new callers follow your fallback routing.</li>
//       <li>Your recent peak of simultaneous calls is shown on the next step so you can size the limit.</li>
//       <li>No commitment.</li>
//     </Box>
//   </Box>
// );

function AddonTermsContent({ description }: { description: string | null }) {
  const content = description?.trim() || '';
  const hasHtml = Boolean(content && /<\/?[a-z][\s\S]*>/i.test(content));

  if (!content) {
    return (
      <Typography fontSize={14} color='text.secondary'>
        No additional terms provided by your agency.
      </Typography>
    );
  }

  if (hasHtml) {
    return (
      <Box
        sx={{
          fontSize: 13,
          color: 'text.secondary',
          lineHeight: 1.55,
          '& h1, & h2, & h3, & h4, & h5, & h6, & p, & strong, & b': {
            color: 'text.primary'
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            fontSize: 14,
            fontWeight: 500,
            mt: 2,
            mb: 1,
            '&:first-of-type': { mt: 0 }
          },
          '& p': { m: 0, mb: 1 },
          '& ul, & ol': { pl: 2.25, m: 0, mb: 1.5 },
          '& li': { mb: 0.75 },
          '& strong, & b': { fontWeight: 500 }
        }}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

  return (
    <Typography fontSize={14} color='text.secondary' whiteSpace='pre-wrap'>
      {content}
    </Typography>
  );
}

function TermsAndOfferingsCollapse({
  description,
  fallback
}: {
  description: string | null;
  fallback?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const hasDescription = Boolean(description?.trim());

  return (
    <Box mt={3.5}>
      <Box
        component='button'
        type='button'
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          border: 0,
          background: 'none',
          cursor: 'pointer',
          p: 0,
          color: '#6B7280',
          '&:hover': { color: '#374151' }
        }}
      >
        {open ? <IoChevronUp size={16} /> : <IoChevronDown size={16} />}
        <Typography
          component='span'
          fontSize={12}
          fontWeight={700}
          letterSpacing={0.5}
          textTransform='uppercase'
          color='inherit'
        >
          Terms and Offerings
        </Typography>
      </Box>
      <Collapse in={open} timeout='auto' unmountOnExit>
        <Box mt={1.5}>{!hasDescription && fallback ? fallback : <AddonTermsContent description={description} />}</Box>
      </Collapse>
    </Box>
  );
}

export default function AddonTermsDrawer({ addon, open, onClose, onActivate }: AddonTermsDrawerProps) {
  if (!addon) return null;

  const active = isAddonActive(addon);
  const canActivate = addon.purchasable && !active;

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: ADDON_DRAWER_WIDTH },
          maxWidth: '100%',
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2}
        borderBottom='1px solid #ECECEC'
      >
        <Typography variant='h6' fontWeight={500}>
          {getAddonDisplayTitle(addon)}
        </Typography>
        <IconButton onClick={onClose} size='small' aria-label='Close'>
          <IoClose size={22} />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={3} py={2.5}>
        <SectionHeading>HOW YOU&apos;LL BE CHARGED</SectionHeading>

        {addon.code === 'priority_support' && <PrioritySupportHowCharged addon={addon} />}
        {addon.code === 'sms_hosting' && <SmsHostingHowCharged addon={addon} />}
        {addon.code === 'a2p_10dlc' && <A2pHowCharged addon={addon} />}
        {addon.code === 'ai_workflow' && <AiWorkflowHowCharged addon={addon} />}
        {addon.code === 'concurrency_limit' && <ConcurrencyLimitHowCharged addon={addon} />}

        <TermsAndOfferingsCollapse key={addon.id} description={addon.description} />
      </Box>

      <Stack direction='row' justifyContent='flex-end' gap={1.5} px={3} py={2} borderTop='1px solid #ECECEC'>
        <Button variant='outlined' color='inherit' onClick={onClose}>
          Cancel
        </Button>
        {canActivate && (
          <Button
            variant='contained'
            onClick={() => {
              onClose();
              onActivate();
            }}
          >
            Activate Add-On
          </Button>
        )}
      </Stack>
    </Drawer>
  );
}
